import React, { useState } from 'react'
import { connectToggleRefinement } from 'react-instantsearch-dom'
import PropTypes from 'prop-types'
import { Box, Accordion, Level, Text, Icon } from '@deseretbook/react-ui'
import { Analytics } from '../../../../dbcom'
import { trackAlgoliaEvent } from '../analytics'
import { ClickableContent } from '../../'

/**
 * Component for toggling boolean refinements (isPlus, onSale)
 *
 * This is a dynamic component that can work with any boolean related values
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/toggle-refinement/react/
 *
 * @param {boolean} currentRefinement - whether the toggle is true/false
 * @param {string} title - Title for toggle section
 * @param {string} label - Label used for the refinement
 * @param {object} count - Counts for checked/unchecked settings
 * @param {function} refine - Updates the refinement
 * @param {function} children - React children, normally used for label
 * @param {boolean} isOpenByDefault - Whether the filter should be open on load
 * @param {string} attribute - The toggle attribute name
 *
 * @returns {function} React Component
 */
const ToggleComponent = ({
  currentRefinement,
  label,
  count,
  refine,
  children,
  isOpenByDefault = false,
  attribute,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault)

  /**
   * Handles/Tracks user applied filter
   *
   */
  const handleClick = () => {
    if (!count.unchecked) {
      return
    }

    refine(!currentRefinement)

    // Track event on GTM
    Analytics.event(
      {
        category: 'Algolia Filter Item',
        action: `User applied filter, ${label}.`,
      },
      {
        isInteractive: true,
      },
    )

    // Track event with Algolia
    trackAlgoliaEvent('clickedFilters', {
      eventName: 'Filter Clicked',
      filters: [`${attribute}:${!currentRefinement}`],
    })
  }

  /**
   * Handles/Tracks Accordion toggle event
   */
  const handleToggle = () => {
    Analytics.event(
      {
        category: 'Algolia Filter',
        action: `User ${isOpen ? 'opened' : 'closed'} the ${label} toggle.`,
      },
      {
        isInteractive: true,
      },
    )

    setIsOpen(!isOpen)
  }

  return (
    <Accordion.Section isOpen={isOpen} onToggle={handleToggle}>
      <Accordion.Section.Header>
        <Box paddingVertical={0.5}>
          <Level>
            <Level.Left>
              <Level.Item>
                <Text style={{ textTransform: 'uppercase' }}>{label}</Text>
              </Level.Item>
            </Level.Left>
            <Level.Right>
              <Level.Item>
                <Icon name={`chevron${isOpen ? 'Up' : 'Down'}`} />
              </Level.Item>
            </Level.Right>
          </Level>
        </Box>
      </Accordion.Section.Header>
      <Accordion.Section.Content>
        <ClickableContent isPaddingless onClick={handleClick} isDisabled={!count.unchecked}>
          <Box marginLeft={0.5} paddingVertical={0.5}>
            <Level>
              <Level.Left>
                <Level.Item>
                  <Icon name={`checkbox${currentRefinement ? 'On' : 'Off'}`} />
                </Level.Item>
                <Level.Item>{children}</Level.Item>
              </Level.Left>
              <Level.Right>
                <Level.Item>({count.unchecked || 0})</Level.Item>
              </Level.Right>
            </Level>
          </Box>
        </ClickableContent>
      </Accordion.Section.Content>
    </Accordion.Section>
  )
}

ToggleComponent.propTypes = {
  currentRefinement: PropTypes.bool,
  count: PropTypes.shape({
    checked: PropTypes.number,
    unchecked: PropTypes.number,
  }),
  label: PropTypes.string,
  refine: PropTypes.func,
  children: PropTypes.func,
  isOpenByDefault: PropTypes.bool,
  attribute: PropTypes.string,
}

export const Toggle = connectToggleRefinement(ToggleComponent)
